/**
 * Point d'entrée XML : détection de syntaxe puis délégation CII / UBL.
 */
import type { ExtractionWarning, InvoiceDocument, Syntax, WarningSeverity } from '../model';
import { decodeXmlBytes, parseXml, rootName, XmlParseError } from './dom';
import { isCii, parseCii } from './cii';
import { isUbl, parseUbl } from './ubl';

export { XmlParseError };

export interface XmlParseOutcome {
  doc: InvoiceDocument;
  warnings: ExtractionWarning[];
}

export function detectSyntax(doc: Document): Syntax {
  if (isCii(doc)) return 'CII';
  if (isUbl(doc)) return 'UBL';
  return 'UNKNOWN';
}

/** Détection rapide (sans DOMParser) pour trier les pièces jointes. */
export function sniffXmlSyntax(bytes: Uint8Array): Syntax | 'ORDER' | 'OTHER' {
  const head = decodeXmlBytes(bytes.subarray(0, Math.min(bytes.length, 8192)));
  if (/<[A-Za-z0-9_.-]*:?CrossIndustryInvoice[\s>]/.test(head)) return 'CII';
  if (/<[A-Za-z0-9_.-]*:?Invoice[\s>]/.test(head)) return 'UBL';
  if (/<[A-Za-z0-9_.-]*:?CreditNote[\s>]/.test(head)) return 'UBL';
  if (/<[A-Za-z0-9_.-]*:?CrossIndustryOrder[\s>]/.test(head)) return 'ORDER';
  if (/<[A-Za-z0-9_.-]*:?Order[\s>]/.test(head)) return 'ORDER';
  return 'OTHER';
}

export function parseInvoiceXml(bytes: Uint8Array): XmlParseOutcome {
  const warnings: ExtractionWarning[] = [];
  const warn = (code: string, message: string, severity: WarningSeverity = 'warn'): void => {
    warnings.push({ code, message, severity });
  };
  let docDom: Document;
  try {
    docDom = parseXml(bytes);
  } catch (err) {
    if (err instanceof XmlParseError) throw err;
    throw new XmlParseError(err instanceof Error ? err.message : 'XML illisible', 'XML_INVALID');
  }
  const syntax = detectSyntax(docDom);
  if (syntax === 'UNKNOWN') {
    const name = rootName(docDom);
    if (name === 'CrossIndustryDocument') {
      throw new XmlParseError(
        'Document ZUGFeRD 1.0 (CrossIndustryDocument) : format obsolète, non pris en charge. Demandez une facture Factur-X ou XRechnung.',
        'ZUGFERD1_UNSUPPORTED',
      );
    }
    if (name === 'CrossIndustryOrder' || name === 'Order') {
      throw new XmlParseError('ORDER-X / commande : ce document n’est pas une facture', 'NOT_AN_INVOICE');
    }
    throw new XmlParseError(`Élément racine non pris en charge : ${name}`, 'NOT_AN_INVOICE');
  }
  return {
    doc: syntax === 'CII' ? parseCii(docDom, warn) : parseUbl(docDom, warn),
    warnings,
  };
}
